// client/src/components/inbox/InboxAutomationPage.jsx
//
// /:tenant/inbox-automation (Roadmap Phase 4):
//   - kill switch: one button turns off every automated reply for the
//     organization (anyone can stop it; owners/admins turn it back on)
//   - keyword auto-reply rules (owners/admins edit; managers see them)
//   - a tester: what would happen to a message
//   - the activity log of every automated reply and skip
//   - saved replies used from the Social Inbox reply box
//
// API: /api/inbox-automation/:tenant (server/routes/inboxAutomationRoutes.js).

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaInbox, FaPlus, FaPowerOff, FaTrash, FaEdit } from 'react-icons/fa';
import AppFooter from '../AppFooter';
import DesktopActionBar from '../DesktopActionBar';
import LoadingState from '../common/LoadingState';
import {
  getAutomationSettings, setAutomationPaused, listRules, createRule, updateRule, deleteRule, testRules,
  getActivityLog, listSavedReplies, createSavedReply, updateSavedReply, deleteSavedReply, errorText,
} from '../../services/inboxAutomationApi';
import '../publishing/Publishing.css';

const NAMES = { facebook: 'Facebook', instagram: 'Instagram', threads: 'Threads', youtube: 'YouTube', bluesky: 'Bluesky', twitter: 'X (Twitter)' };
const HOURS = Array.from({ length: 24 }, (_, h) => h);

function when(value) {
  if (!value) return '';
  const d = new Date(String(value).replace(' ', 'T') + (String(value).includes('T') ? '' : 'Z'));
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
}
function hourLabel(h) {
  return new Date(Date.UTC(2030, 0, 1, h)).toLocaleTimeString(undefined, { timeZone: 'UTC', hour: 'numeric' });
}

const EMPTY_RULE = { name: '', keywords: '', matchMode: 'any', channels: [], replyText: '', dmText: '', quietStart: '', quietEnd: '', dailyCap: 50, priority: 100, active: true };

function RuleForm({ initial, channels, onSave, onCancel, t }) {
  const [f, setF] = useState(() => (initial
    ? { ...initial, keywords: initial.keywords.join(', '), quietStart: initial.quietStart ?? '', quietEnd: initial.quietEnd ?? '', replyText: initial.replyText || '', dmText: initial.dmText || '' }
    : EMPTY_RULE));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const toggle = (key) => set('channels', f.channels.includes(key) ? f.channels.filter((c) => c !== key) : [...f.channels, key]);
  const metaComments = f.channels.some((c) => c === 'facebook:comment' || c === 'instagram:comment');
  const byKind = (kind) => channels.filter((c) => c.kind === kind);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await onSave({
        name: f.name, keywords: f.keywords.split(',').map((k) => k.trim()).filter(Boolean), matchMode: f.matchMode,
        channels: f.channels, replyText: f.replyText, dmText: metaComments ? f.dmText : '',
        quietStart: f.quietStart === '' ? null : Number(f.quietStart), quietEnd: f.quietEnd === '' ? null : Number(f.quietEnd),
        dailyCap: Number(f.dailyCap), priority: Number(f.priority), active: Boolean(f.active),
      });
    } catch (err) {
      setError(errorText(err, t('inboxAutomation.rules.saveError', 'The rule could not be saved.')));
    } finally { setBusy(false); }
  };

  return (
    <form className="pb-form" onSubmit={submit} data-testid="rule-form">
      <label className="pb-field" htmlFor="ia-name">
        {t('inboxAutomation.rules.name', 'Rule name')}
        <input id="ia-name" type="text" maxLength={120} required value={f.name} onChange={(e) => set('name', e.target.value)} placeholder={t('inboxAutomation.rules.namePlaceholder', 'e.g. Ticket link')} />
      </label>
      <label className="pb-field" htmlFor="ia-keywords">
        {t('inboxAutomation.rules.keywords', 'Keywords (comma-separated)')}
        <input id="ia-keywords" type="text" required value={f.keywords} onChange={(e) => set('keywords', e.target.value)} placeholder="tickets, ticket link" />
      </label>
      <label className="pb-field" htmlFor="ia-match">
        {t('inboxAutomation.rules.match', 'Match when the message')}
        <select id="ia-match" value={f.matchMode} onChange={(e) => set('matchMode', e.target.value)}>
          <option value="any">{t('inboxAutomation.rules.matchAny', 'contains any keyword')}</option>
          <option value="all">{t('inboxAutomation.rules.matchAll', 'contains every keyword')}</option>
          <option value="exact">{t('inboxAutomation.rules.matchExact', 'is exactly a keyword')}</option>
        </select>
      </label>
      <fieldset className="pb-field pb-wide" style={{ border: 0, padding: 0, margin: 0 }}>
        <legend>{t('inboxAutomation.rules.commentsOn', 'Listen to comments on')}</legend>
        <div className="pb-checks">
          {byKind('comment').map((c) => (
            <label key={c.key}>
              <input type="checkbox" checked={f.channels.includes(c.key)} onChange={() => toggle(c.key)} />
              {NAMES[c.platform] || c.platform}
              {!c.connected && <span className="pb-badge muted">{t('inboxAutomation.notConnected', 'not connected')}</span>}
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset className="pb-field pb-wide" style={{ border: 0, padding: 0, margin: 0 }}>
        <legend>{t('inboxAutomation.rules.messagesOn', 'Listen to direct messages on')}</legend>
        <div className="pb-checks">
          {byKind('dm').map((c) => (
            <label key={c.key}>
              <input type="checkbox" checked={f.channels.includes(c.key)} onChange={() => toggle(c.key)} />
              {NAMES[c.platform] || c.platform}
              {c.pendingApproval && <span className="pb-badge warn">{t('inboxAutomation.pendingMeta', 'pending Meta approval')}</span>}
            </label>
          ))}
        </div>
      </fieldset>
      <label className="pb-field pb-wide" htmlFor="ia-reply">
        {t('inboxAutomation.rules.reply', 'Reply (public reply under a comment, or the answer to a message)')}
        <textarea id="ia-reply" rows={2} maxLength={1000} value={f.replyText} onChange={(e) => set('replyText', e.target.value)} placeholder={t('inboxAutomation.rules.replyPlaceholder', 'Thanks {name}! Check your messages for the link.')} />
      </label>
      {metaComments && (
        <label className="pb-field pb-wide" htmlFor="ia-dm">
          {t('inboxAutomation.rules.dm', 'Private message to the person who commented (optional)')}
          <textarea id="ia-dm" rows={2} maxLength={1000} value={f.dmText} onChange={(e) => set('dmText', e.target.value)} placeholder={t('inboxAutomation.rules.dmPlaceholder', 'Here’s your ticket link: https://…')} />
          <span className="pb-ai-meta">{t('inboxAutomation.rules.dmHelp', 'Facebook and Instagram only, once per comment, within 7 days of it. Needs Meta’s messaging approval for your account.')}</span>
        </label>
      )}
      <label className="pb-field" htmlFor="ia-qs">
        {t('inboxAutomation.rules.quietFrom', 'Quiet hours from')}
        <select id="ia-qs" value={f.quietStart} onChange={(e) => set('quietStart', e.target.value)}>
          <option value="">{t('inboxAutomation.rules.noQuiet', 'No quiet hours')}</option>
          {HOURS.map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
        </select>
      </label>
      <label className="pb-field" htmlFor="ia-qe">
        {t('inboxAutomation.rules.quietTo', 'until')}
        <select id="ia-qe" value={f.quietEnd} onChange={(e) => set('quietEnd', e.target.value)} disabled={f.quietStart === ''}>
          <option value="">—</option>
          {HOURS.map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
        </select>
      </label>
      <label className="pb-field" htmlFor="ia-cap">
        {t('inboxAutomation.rules.cap', 'Most replies per day')}
        <input id="ia-cap" type="number" min={1} max={500} value={f.dailyCap} onChange={(e) => set('dailyCap', e.target.value)} />
      </label>
      <label className="pb-field" htmlFor="ia-priority">
        {t('inboxAutomation.rules.priority', 'Order (lower runs first)')}
        <input id="ia-priority" type="number" min={0} max={10000} value={f.priority} onChange={(e) => set('priority', e.target.value)} />
      </label>
      <label className="pb-checks pb-wide">
        <input type="checkbox" checked={f.active} onChange={(e) => set('active', e.target.checked)} /> {t('inboxAutomation.rules.on', 'Rule is on')}
      </label>
      {error && <div className="note error pb-wide" role="alert">{error}</div>}
      <div className="pb-actions pb-wide">
        <button type="submit" className="pb-btn primary" disabled={busy}>{t('inboxAutomation.rules.save', 'Save rule')}</button>
        <button type="button" className="pb-btn" onClick={onCancel}>{t('inboxAutomation.cancel', 'Cancel')}</button>
      </div>
    </form>
  );
}

export default function InboxAutomationPage({ tenantId: tenantProp }) {
  const { tenant: tenantParam } = useParams();
  const tenant = tenantParam || tenantProp;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [rules, setRules] = useState(null);
  const [rulesDenied, setRulesDenied] = useState(false);
  const [editing, setEditing] = useState(null); // null | 'new' | rule
  const [log, setLog] = useState(null);
  const [logNext, setLogNext] = useState(null);
  const [logStatus, setLogStatus] = useState('');
  const [saved, setSaved] = useState([]);
  const [savedEdit, setSavedEdit] = useState(null); // null | {} | reply
  const [test, setTest] = useState({ text: '', channel: 'instagram:comment', includeInactive: true });
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    try {
      setSettings(await getAutomationSettings(tenant));
    } catch (err) { setError(errorText(err, t('inboxAutomation.loadError', 'Could not load automation settings.'))); }
    try {
      const r = await listRules(tenant);
      setRules(r.rules || []);
    } catch (err) {
      if (err?.response?.status === 403) setRulesDenied(true);
      setRules([]);
    }
    listSavedReplies(tenant).then((r) => setSaved(r.replies || [])).catch(() => setSaved([]));
  }, [tenant, t]);

  const loadLog = useCallback(async (more = false) => {
    try {
      const r = await getActivityLog(tenant, { before: more ? logNext : null, status: logStatus });
      setLog((l) => (more && l ? [...l, ...r.entries] : r.entries || []));
      setLogNext(r.next);
    } catch { setLog([]); }
  }, [tenant, logNext, logStatus]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!rulesDenied) loadLog(false); }, [logStatus, rulesDenied]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePause = async () => {
    setError(''); setNotice('');
    try {
      const paused = !settings.paused;
      await setAutomationPaused(tenant, paused);
      setSettings((s) => ({ ...s, paused }));
      setNotice(paused ? t('inboxAutomation.pausedNotice', 'All automation is off. Nothing will be sent automatically until it is turned back on.') : t('inboxAutomation.resumedNotice', 'Automation is back on.'));
    } catch (err) { setError(errorText(err, t('inboxAutomation.pauseError', 'Could not change automation.'))); }
  };

  const saveRule = async (body) => {
    if (editing === 'new') await createRule(tenant, body); else await updateRule(tenant, editing.id, body);
    setEditing(null);
    await load();
  };
  const toggleRule = async (r) => {
    try { await updateRule(tenant, r.id, { active: !r.active }); await load(); } catch (err) { setError(errorText(err, t('inboxAutomation.rules.saveError', 'The rule could not be saved.'))); }
  };
  const removeRule = async (r) => {
    if (!window.confirm(t('inboxAutomation.rules.confirmDelete', 'Delete the rule “{{name}}”?', { name: r.name }))) return;
    try { await deleteRule(tenant, r.id); await load(); } catch (err) { setError(errorText(err, t('inboxAutomation.rules.saveError', 'The rule could not be saved.'))); }
  };
  const runTest = async (e) => {
    e.preventDefault();
    try { setTestResult(await testRules(tenant, test)); } catch (err) { setTestResult({ error: errorText(err, t('inboxAutomation.test.error', 'Could not test.')) }); }
  };
  const saveSaved = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const body = { title: savedEdit.title || '', body: savedEdit.body || '' };
      if (savedEdit.id) await updateSavedReply(tenant, savedEdit.id, body); else await createSavedReply(tenant, body);
      setSavedEdit(null);
      const r = await listSavedReplies(tenant);
      setSaved(r.replies || []);
    } catch (err) { setError(errorText(err, t('inboxAutomation.saved.saveError', 'The saved reply could not be saved.'))); }
  };
  const removeSaved = async (s) => {
    if (!window.confirm(t('inboxAutomation.saved.confirmDelete', 'Delete the saved reply “{{title}}”?', { title: s.title }))) return;
    try { await deleteSavedReply(tenant, s.id); setSaved((l) => l.filter((x) => x.id !== s.id)); } catch (err) { setError(errorText(err, t('inboxAutomation.saved.saveError', 'The saved reply could not be saved.'))); }
  };

  const channelLabel = (key) => {
    const [p, kind] = key.split(':');
    return `${NAMES[p] || p} ${kind === 'dm' ? t('inboxAutomation.messages', 'messages') : t('inboxAutomation.comments', 'comments')}`;
  };
  const actionLabel = {
    reply: t('inboxAutomation.action.reply', 'Public reply'),
    private_dm: t('inboxAutomation.action.privateDm', 'Private message'),
    dm_reply: t('inboxAutomation.action.dmReply', 'Message reply'),
  };
  const statusLabel = {
    sent: t('inboxAutomation.status.sent', 'Sent'),
    skipped: t('inboxAutomation.status.skipped', 'Skipped'),
    failed: t('inboxAutomation.status.failed', 'Failed'),
  };
  const buttons = [
    { icon: <FaArrowLeft />, label: t('common.back', 'Back'), onClick: () => navigate(-1) },
    { icon: <FaInbox />, label: t('inboxAutomation.inbox', 'Social Inbox'), onClick: () => navigate(`/${tenant}/replies`) },
  ];
  const channels = (settings && settings.channels) || [];

  return (
    <div className="pb-page">
      <DesktopActionBar buttons={buttons} />
      <section className="surface">
        <h2>{t('inboxAutomation.title', 'Inbox automation')}</h2>
        <p className="surface-muted">
          {t('inboxAutomation.intro', 'Answer common questions automatically: when a comment or message contains your keywords, On-Screen replies for you, and can send the person a private message (for example “Comment TICKETS and we’ll message you the link”). Every automatic reply is listed in the activity log, and one switch turns it all off.')}
        </p>
      </section>

      {!settings && !error && <LoadingState />}
      {error && <div className="note error" role="alert">{error}</div>}
      {notice && <div className="note ok" role="status">{notice}</div>}
      {settings && !settings.ready && <div className="note warn" role="status">{t('inboxAutomation.migration', 'Inbox automation needs a database update before it can be used.')}</div>}

      {settings && settings.ready && (
        <section className={`note ${settings.paused ? 'warn' : 'ok'}`} data-testid="kill-switch">
          <div className="pb-actions" style={{ justifyContent: 'space-between' }}>
            <strong>
              {settings.paused
                ? t('inboxAutomation.isPaused', 'Automation is OFF. Nothing is sent automatically.')
                : t('inboxAutomation.isOn', 'Automation is on. Active rules answer new comments and messages.')}
            </strong>
            <button type="button" className={`pb-btn ${settings.paused ? 'primary' : 'danger'}`} onClick={togglePause}>
              <FaPowerOff aria-hidden="true" /> {settings.paused ? t('inboxAutomation.resume', 'Turn automation back on') : t('inboxAutomation.pause', 'Turn off all automation')}
            </button>
          </div>
        </section>
      )}

      {settings && settings.ready && !rulesDenied && (
        <section className="surface" data-testid="rules">
          <div className="pb-actions" style={{ justifyContent: 'space-between' }}>
            <h3 style={{ margin: 0 }}>{t('inboxAutomation.rules.title', 'Keyword auto-replies')}</h3>
            {editing === null && (
              <button type="button" className="pb-btn primary" onClick={() => setEditing('new')}><FaPlus aria-hidden="true" /> {t('inboxAutomation.rules.new', 'New rule')}</button>
            )}
          </div>
          <p className="pb-ai-meta">{t('inboxAutomation.rules.help', 'Only new comments and messages (from the last 24 hours, after the rule was made) are answered, each once. Someone who writes the keyword again the same day isn’t answered twice by the same rule.')}</p>
          {editing !== null && (
            <RuleForm initial={editing === 'new' ? null : editing} channels={channels} onSave={saveRule} onCancel={() => setEditing(null)} t={t} />
          )}
          {rules && rules.length === 0 && editing === null && <p className="surface-muted">{t('inboxAutomation.rules.none', 'No rules yet.')}</p>}
          <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
            {(rules || []).map((r) => (
              <article key={r.id} className={`pb-feed${r.active ? '' : ' paused'}`} data-testid="rule">
                <p className="pb-feed-title">{r.name}</p>
                <div className="pb-feed-meta">
                  <span className={`pb-badge ${r.active ? 'ok' : 'muted'}`}>{r.active ? t('inboxAutomation.rules.active', 'On') : t('inboxAutomation.rules.off', 'Off')}</span>
                  <span>{t('inboxAutomation.rules.keywordsList', 'Keywords: {{list}}', { list: r.keywords.join(', ') })}</span>
                  <span>{r.channels.map(channelLabel).join(', ')}</span>
                  <span>{t('inboxAutomation.rules.sentToday', '{{sent}} of {{cap}} today', { sent: r.sentToday || 0, cap: r.dailyCap })}</span>
                  {r.quietStart !== null && <span>{t('inboxAutomation.rules.quietRange', 'Quiet {{from}}–{{to}}', { from: hourLabel(r.quietStart), to: hourLabel(r.quietEnd) })}</span>}
                </div>
                {r.replyText && <div className="pb-ai-meta">{actionLabel.reply}: “{r.replyText}”</div>}
                {r.dmText && <div className="pb-ai-meta">{actionLabel.private_dm}: “{r.dmText}”</div>}
                <div className="pb-actions">
                  <button type="button" className="pb-btn" onClick={() => toggleRule(r)}>{r.active ? t('inboxAutomation.rules.turnOff', 'Turn off') : t('inboxAutomation.rules.turnOn', 'Turn on')}</button>
                  <button type="button" className="pb-btn" onClick={() => setEditing(r)}><FaEdit aria-hidden="true" /> {t('inboxAutomation.edit', 'Edit')}</button>
                  <button type="button" className="pb-btn danger" onClick={() => removeRule(r)}><FaTrash aria-hidden="true" /> {t('inboxAutomation.delete', 'Delete')}</button>
                </div>
              </article>
            ))}
          </div>

          <form className="pb-form" onSubmit={runTest} style={{ marginTop: 12 }} data-testid="rule-test">
            <h4 className="pb-wide" style={{ margin: 0, color: '#23324d' }}>{t('inboxAutomation.test.title', 'Try a message')}</h4>
            <label className="pb-field" htmlFor="ia-test-text">
              {t('inboxAutomation.test.text', 'Message')}
              <input id="ia-test-text" type="text" value={test.text} onChange={(e) => setTest((x) => ({ ...x, text: e.target.value }))} placeholder="TICKETS" />
            </label>
            <label className="pb-field" htmlFor="ia-test-channel">
              {t('inboxAutomation.test.channel', 'Arrives as')}
              <select id="ia-test-channel" value={test.channel} onChange={(e) => setTest((x) => ({ ...x, channel: e.target.value }))}>
                {channels.map((c) => <option key={c.key} value={c.key}>{channelLabel(c.key)}</option>)}
              </select>
            </label>
            <div className="pb-actions pb-wide">
              <button type="submit" className="pb-btn" disabled={!test.text.trim()}>{t('inboxAutomation.test.run', 'Test')}</button>
            </div>
            {testResult && (
              <div className="pb-preview pb-wide" role="status" data-testid="test-result">
                {testResult.error && testResult.error}
                {!testResult.error && !testResult.matched && t('inboxAutomation.test.noMatch', 'No rule answers this message.')}
                {testResult.matched && (
                  <>
                    <strong>{t('inboxAutomation.test.matched', 'Answered by “{{name}}”:', { name: testResult.rule.name })}</strong>
                    <ul>{testResult.actions.map((a) => <li key={a.action}>{actionLabel[a.action]}: “{a.text}”</li>)}</ul>
                  </>
                )}
              </div>
            )}
          </form>
        </section>
      )}

      {settings && settings.ready && !rulesDenied && (
        <section className="surface" data-testid="activity-log">
          <div className="pb-actions" style={{ justifyContent: 'space-between' }}>
            <h3 style={{ margin: 0 }}>{t('inboxAutomation.log.title', 'Activity log')}</h3>
            <label className="pb-field" htmlFor="ia-log-status">
              <span className="pb-ai-meta">{t('inboxAutomation.log.show', 'Show')}</span>
              <select id="ia-log-status" value={logStatus} onChange={(e) => setLogStatus(e.target.value)}>
                <option value="">{t('inboxAutomation.log.all', 'Everything')}</option>
                <option value="sent">{statusLabel.sent}</option>
                <option value="skipped">{statusLabel.skipped}</option>
                <option value="failed">{statusLabel.failed}</option>
              </select>
            </label>
          </div>
          {log && log.length === 0 && <p className="surface-muted">{t('inboxAutomation.log.none', 'Nothing has been sent automatically yet.')}</p>}
          {log && log.length > 0 && (
            <div className="pb-table-wrap">
              <table className="pb-table">
                <thead>
                  <tr>
                    <th scope="col">{t('inboxAutomation.log.when', 'When')}</th>
                    <th scope="col">{t('inboxAutomation.log.rule', 'Rule')}</th>
                    <th scope="col">{t('inboxAutomation.log.where', 'Where')}</th>
                    <th scope="col">{t('inboxAutomation.log.message', 'Message')}</th>
                    <th scope="col">{t('inboxAutomation.log.what', 'What happened')}</th>
                  </tr>
                </thead>
                <tbody>
                  {log.map((e) => (
                    <tr key={e.id}>
                      <td>{when(e.createdOn)}</td>
                      <td>{e.ruleName || '—'}</td>
                      <td>{channelLabel(`${e.platform}:${e.channel}`)}</td>
                      <td className="pb-content">{e.author ? <strong>{e.author}: </strong> : null}{e.message}</td>
                      <td>
                        <span className={`pb-badge ${e.status === 'sent' ? 'ok' : e.status === 'failed' ? 'err' : 'muted'}`}>{statusLabel[e.status]}</span>{' '}
                        {actionLabel[e.action] || e.action}
                        {e.reason && <div className="pb-ai-meta">{e.reason}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {logNext && <button type="button" className="pb-btn" style={{ marginTop: 8 }} onClick={() => loadLog(true)}>{t('inboxAutomation.log.more', 'Show older')}</button>}
        </section>
      )}

      {rulesDenied && <div className="note info" role="status">{t('inboxAutomation.rules.denied', 'Owners, admins and managers set up automatic replies. You can use saved replies from the Social Inbox.')}</div>}

      {settings && settings.ready && (
        <section className="surface" data-testid="saved-replies">
          <div className="pb-actions" style={{ justifyContent: 'space-between' }}>
            <h3 style={{ margin: 0 }}>{t('inboxAutomation.saved.title', 'Saved replies')}</h3>
            {!rulesDenied && savedEdit === null && <button type="button" className="pb-btn primary" onClick={() => setSavedEdit({})}><FaPlus aria-hidden="true" /> {t('inboxAutomation.saved.new', 'New saved reply')}</button>}
          </div>
          <p className="pb-ai-meta">{t('inboxAutomation.saved.help', 'Answers your team uses often. Pick one from “Saved replies…” in the Social Inbox reply box; {name} becomes the person’s name.')}</p>
          {savedEdit !== null && (
            <form className="pb-form" onSubmit={saveSaved} data-testid="saved-form">
              <label className="pb-field" htmlFor="ia-saved-title">
                {t('inboxAutomation.saved.titleField', 'Title')}
                <input id="ia-saved-title" type="text" maxLength={120} required value={savedEdit.title || ''} onChange={(e) => setSavedEdit((x) => ({ ...x, title: e.target.value }))} />
              </label>
              <label className="pb-field pb-wide" htmlFor="ia-saved-body">
                {t('inboxAutomation.saved.body', 'Reply')}
                <textarea id="ia-saved-body" rows={3} maxLength={2000} required value={savedEdit.body || ''} onChange={(e) => setSavedEdit((x) => ({ ...x, body: e.target.value }))} />
              </label>
              <div className="pb-actions pb-wide">
                <button type="submit" className="pb-btn primary">{t('inboxAutomation.saved.save', 'Save')}</button>
                <button type="button" className="pb-btn" onClick={() => setSavedEdit(null)}>{t('inboxAutomation.cancel', 'Cancel')}</button>
              </div>
            </form>
          )}
          {saved.length === 0 && savedEdit === null && <p className="surface-muted">{t('inboxAutomation.saved.none', 'No saved replies yet.')}</p>}
          <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
            {saved.map((s) => (
              <article key={s.id} className="pb-feed" data-testid="saved-reply">
                <p className="pb-feed-title">{s.title}</p>
                <div className="pb-ai-text">{s.body}</div>
                <div className="pb-feed-meta"><span>{t('inboxAutomation.saved.used', 'Used {{count}} time(s)', { count: s.useCount })}</span></div>
                {!rulesDenied && (
                  <div className="pb-actions">
                    <button type="button" className="pb-btn" onClick={() => setSavedEdit(s)}><FaEdit aria-hidden="true" /> {t('inboxAutomation.edit', 'Edit')}</button>
                    <button type="button" className="pb-btn danger" onClick={() => removeSaved(s)}><FaTrash aria-hidden="true" /> {t('inboxAutomation.delete', 'Delete')}</button>
                  </div>
                )}
              </article>
            ))}
          </div>
        </section>
      )}

      <AppFooter buttons={buttons} />
    </div>
  );
}
